import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Save, Trash2, AlertTriangle } from "lucide-react";

/** Recalls inline authoring — combines ingested + admin-authored recalls. */
export default function CmsRecalls() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await http.get("/recalls", { params: { limit: 100 } });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load recalls");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    try {
      await http.patch(`/admin/cms/recalls/${r.id}`, {
        title: r.title,
        summary: r.summary,
        link: r.link,
        status: r.status,
        admin_note: r.admin_note,
        brands: r.brand_hits,
      });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (rid) => {
    if (!window.confirm("Delete recall?")) return;
    try {
      await http.delete(`/admin/cms/recalls/${rid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const create = async () => {
    if (!draft.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setBusy(true);
    try {
      await http.post("/admin/cms/recalls", {
        title: draft.title.trim(),
        summary: draft.summary,
        link: draft.link || null,
        country_hint: draft.country_hint || null,
        brands: draft.brands.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Recall published");
      setDraft(emptyDraft());
      setShowNew(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl" data-testid="cms-recalls">
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Recalls</h1>
          <p className="mt-1 text-sm text-slate-500">Ingested from FDA/USDA plus admin-authored alerts. {items.length} loaded.</p>
        </div>
        <Button onClick={() => setShowNew((v) => !v)} data-testid="cms-recall-new-toggle" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="mr-1 h-4 w-4" /> {showNew ? "Cancel" : "New recall"}
        </Button>
      </div>

      {showNew && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="cms-recall-new-form">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FieldFull label="Title *"><Input data-testid="cms-recall-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></FieldFull>
            <FieldFull label="Source URL"><Input value={draft.link} onChange={(e) => setDraft({ ...draft, link: e.target.value })} /></FieldFull>
            <FieldFull label="Country hint (US, IN, etc.)"><Input value={draft.country_hint} onChange={(e) => setDraft({ ...draft, country_hint: e.target.value })} /></FieldFull>
            <FieldFull label="Brands (comma separated)"><Input value={draft.brands} onChange={(e) => setDraft({ ...draft, brands: e.target.value })} /></FieldFull>
            <FieldFull label="Summary" full><Textarea rows={4} value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} /></FieldFull>
          </div>
          <div className="mt-2 flex justify-end">
            <Button onClick={create} disabled={busy} data-testid="cms-recall-create" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="mr-1 h-4 w-4" /> {busy ? "Publishing…" : "Publish recall"}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {items.map((r) => (
          <RecallCard key={r.id} r={r} onSave={save} onRemove={remove} />
        ))}
        {items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No recalls loaded.
          </div>
        )}
      </div>
    </div>
  );
}

function RecallCard({ r, onSave, onRemove }) {
  const [draft, setDraft] = useState(r);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setDraft(r); setDirty(false); }, [r]);
  const set = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cms-recall-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
            <Input value={draft.title} onChange={(e) => set("title", e.target.value)} className="border-0 bg-transparent p-0 text-sm font-semibold focus-visible:ring-0" />
          </div>
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
            <span>Source: {r.source || "—"}</span>
            <span>·</span>
            <span>Seen: {r.last_seen_at?.slice(0, 10) || "—"}</span>
            {r.link && <a href={r.link} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">Open source ↗</a>}
          </div>
        </div>
        <select
          value={draft.status || "active"}
          onChange={(e) => set("status", e.target.value)}
          className="rounded-md border border-slate-200 px-2 py-1 text-xs"
        >
          <option value="active">active</option>
          <option value="stale">stale</option>
          <option value="withdrawn">withdrawn</option>
        </select>
      </div>
      <Textarea rows={3} value={draft.summary || ""} onChange={(e) => set("summary", e.target.value)} className="mt-2 text-xs" />
      {dirty && (
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={() => onRemove(r.id)} className="text-xs text-rose-600 hover:underline">Delete</button>
          <Button size="sm" onClick={() => onSave(draft)} data-testid={`cms-recall-save-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Save className="mr-1 h-3 w-3" /> Save
          </Button>
        </div>
      )}
    </div>
  );
}

function FieldFull({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="mb-1 block text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}

function emptyDraft() {
  return { title: "", summary: "", link: "", country_hint: "US", brands: "" };
}
